/*
 * File: app-slice.ts
 * Project: receiving-rules
 * -----
 * Last Modified: 30 Sep 2020 5:38:56 pm
 * -----
 * Copyright (c) 2020 Elegant Software Solutions All rights reserved.
 */

import { GetTokenSilentlyOptions } from '@auth0/auth0-spa-js';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import Rollbar from 'rollbar';
import { ErrorModel } from '../../models/ErrorModel';
import { LinesModel } from '../../models/LinesModel';
import { LinesToRegisterModel } from '../../models/LinesToRegisterModel';
import { PackingSlipModel } from '../../models/PackingSlipModel';
import { POLineNumberQueryModel } from '../../models/POLineNumberQueryModel';
import { POReceiptInputModel } from '../../models/POReceiptInputModel';
import { POReceiptModel } from '../../models/POReceiptModel';
import { POReceiptsStateModel } from '../../models/POReceiptsStateModel';
import { POStagedTableModel } from '../../models/POStagedTableModel';
import { StagedTableModel } from '../../models/StagedTableModel';
import { ThunkApiModel } from '../../models/ThunkApiModel';
import { POReceiptService } from '../../services/POReceiptService';

const initialState: POReceiptsStateModel = {
  allReceiptLines: null,
  dateRange: [],
  loading: false,
  lineNumberLoading: false,
  packingSlipSubmissionLoading: false,
  error: null,
  fetchLineError: null,
  currentlyFiltering: false,
  clearPOSubmissionData: false,
  filteredCUSTPONumberInput: '',
  filteredMDSIPONumberInput: '',
  filteredVendorAccountInput: '',
  filteredVendorNameInput: '',
  filteredWarehouseInput: '',
  offset: 0,
  POReceiptList: [],
  POReceiptListFiltered: [],
  selectedLine: null,
  selectedReceipt: null,
  selectedMenu: '',
  stagedTable: [],
  totalPOReceipts: 0,
  totalCalls: 0,
  selectedToDelete: [],
  selectedKeysToDelete: [],
  serialNumbers: []
};

export const asyncFetchPOReceipts = createAsyncThunk<
  POReceiptModel[],
  { getAccessTokenSilently: (options?: GetTokenSilentlyOptions | undefined) => Promise<string>; offset: number; callAgain: boolean },
  ThunkApiModel
>('poReceipts/asyncFetchPOReceipts', async (params, thunkApi) => {
  const { dispatch, getState, rejectWithValue } = thunkApi;
  const { app, poReceipts } = getState();

  const dataAreaId = app.acuityContext?.selectedCustomer.business.dataAreaId;
  const token = await params.getAccessTokenSilently();
  const queryParameters: POReceiptInputModel = {
    custPONumber: poReceipts.filteredCUSTPONumberInput,
    mdsiPONumber: poReceipts.filteredMDSIPONumberInput,
    startDate: poReceipts.dateRange.length ? poReceipts.dateRange[0] : '',
    endDate: poReceipts.dateRange.length ? poReceipts.dateRange[1] : '',
    vendorAccount: poReceipts.filteredVendorAccountInput,
    vendorName: poReceipts.filteredVendorNameInput,
    warehouse: poReceipts.filteredWarehouseInput
  };
  const response = await POReceiptService.getPOReceipts(token, dataAreaId as string, params.offset, queryParameters);

  if (response.error !== null) {
    const error: ErrorModel = {
      ...response.error,
      ...{ status: response.status ?? 500, userInfo: { userId: app.user?.sub ?? 'user id undefined' }, actionType: 'poReceipts/asyncFetchPOReceipts' as string }
    };

    return rejectWithValue(error as ErrorModel);
  }

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  if (response.data !== null) dispatch(setTotalPOReceipts(response.data.totalCount));

  return response.data?.data as POReceiptModel[];
});

export const asyncFetchPOLineNumber = createAsyncThunk<
  POReceiptModel,
  { getAccessTokenSilently: (options?: GetTokenSilentlyOptions | undefined) => Promise<string>; POLineNumber?: string },
  ThunkApiModel
>('poReceipts/asyncFetchPOLineNumber', async (params, thunkApi) => {
  const { getState, rejectWithValue } = thunkApi;
  const { app, poReceipts } = getState();

  const dataAreaId = app.acuityContext?.selectedCustomer.business.dataAreaId;
  const token = await params.getAccessTokenSilently();
  const queryParameters: POLineNumberQueryModel = {
    MDSIPONumber: typeof params.POLineNumber === 'undefined' ? (poReceipts.selectedReceipt?.purchaseOrderNumber as string) : params.POLineNumber
  };
  const response = await POReceiptService.getPOLineNumber(token, dataAreaId as string, queryParameters);

  if (response.error !== null) {
    const error: ErrorModel = {
      ...response.error,
      ...{ status: response.status ?? 500, userInfo: { userId: app.user?.sub ?? 'user id undefined' }, actionType: 'poReceipts/asyncFetchPOLineNumber' as string }
    };

    return rejectWithValue(error as ErrorModel);
  }

  return response.data as POReceiptModel;
});

export const asyncSubmitPackingSlip = createAsyncThunk<
  string,
  { getAccessTokenSilently: (options?: GetTokenSilentlyOptions | undefined) => Promise<string>; packingSlip: string; trackingNumbers: string[]; productReceiptDate: string; deliveryMode: string },
  ThunkApiModel
>('poReceipts/asyncSubmitPackingSlip', async (params, thunkApi) => {
  const { dispatch, getState, rejectWithValue } = thunkApi;
  const { app, poReceipts, selectedLegalEntity } = getState();

  const dataAreaId = selectedLegalEntity.selectedLegalEntity;
  const token = await params.getAccessTokenSilently();
  const queryParameters: POLineNumberQueryModel = {
    MDSIPONumber: poReceipts.selectedReceipt?.purchaseOrderNumber as string
  };

  const linesToRegister: LinesToRegisterModel[] = poReceipts.stagedTable.map((group: { serialNumbers: any; line: string; quantity: string; binLocation: string; warehouse: string }) => ({
    lineNumber: parseInt(group.line),
    warehouseId: group.warehouse,
    locationId: group.binLocation,
    quantity: Number(group.quantity),
    serialNumbers: group.serialNumbers
  }));

  const data: PackingSlipModel = {
    linesToRegister: linesToRegister,
    packingSlip: params.packingSlip,
    trackingNumbers: params.trackingNumbers
  };
  const response = await POReceiptService.submitPackingSlip(token, dataAreaId as string, queryParameters, data);

  if (response.error !== null) {
    const error: ErrorModel = {
      ...response.error,
      ...{ status: response.status ?? 500, userInfo: { userId: app.user?.sub ?? 'user id undefined' }, actionType: 'poReceipts/asyncFetchPOLineNumber' as string }
    };

    return rejectWithValue(error as ErrorModel);
  }

  const getAccessTokenSilently = params.getAccessTokenSilently;

  dispatch(
    asyncFetchPOLineNumber({
      getAccessTokenSilently
    })
  );

  return 'success';
});

export const poReceiptsSlice = createSlice({
  name: 'poReceipts',
  initialState,
  reducers: {
    setDateRange(state, action: PayloadAction<string[]>): void {
      state.dateRange = action.payload;
    },
    setClearPOSubmissionData(state, action: PayloadAction<boolean>): void {
      state.clearPOSubmissionData = action.payload;
    },
    setLoading(state, action: PayloadAction<boolean>): void {
      state.loading = action.payload;
    },
    setFilteredMDSIPONumberInput(state, action: PayloadAction<string>): void {
      state.filteredMDSIPONumberInput = action.payload;
    },
    setFilteredCUSTPONumberInput(state, action: PayloadAction<string>): void {
      state.filteredCUSTPONumberInput = action.payload;
    },
    setFilteredVendorAccountInput(state, action: PayloadAction<string>): void {
      state.filteredVendorAccountInput = action.payload;
    },
    setFilteredVendorNameInput(state, action: PayloadAction<string>): void {
      state.filteredVendorNameInput = action.payload;
    },
    setFilteredWarehouseInput(state, action: PayloadAction<string>): void {
      state.filteredWarehouseInput = action.payload;
    },
    setFetchLineError(state, action: PayloadAction<ErrorModel | null>): void {
      state.fetchLineError = action.payload;
    },
    setOffset(state, action: PayloadAction<number>): void {
      state.offset = action.payload;
    },
    setPOReceiptList(state, action: PayloadAction<POReceiptModel[]>): void {
      state.POReceiptList = action.payload;
    },
    setPOReceiptListFiltered(state, action: PayloadAction<POReceiptModel[]>): void {
      state.POReceiptListFiltered = action.payload;
    },
    setSelectedLine(state, action: PayloadAction<LinesModel | null>): void {
      state.selectedLine = action.payload;
    },
    setSelectedMenu(state, action: PayloadAction<string>): void {
      state.selectedMenu = action.payload;
    },
    setSelectedToDelete(state, action: PayloadAction<POStagedTableModel[]>): void {
      state.selectedToDelete = action.payload;
    },
    setSelectedKeysToDelete(state, action: PayloadAction<number[]>): void {
      state.selectedKeysToDelete = action.payload;
    },
    setSerialNumbers(state, action: PayloadAction<string[]>): void {
      state.serialNumbers = [...state.serialNumbers, ...action.payload];
    },
    removerSerialNumber(state, action: PayloadAction<string>): void {
      state.serialNumbers = state.serialNumbers.filter((serial) => serial !== action.payload);
    },
    removeAllSerialNumbers(state): void {
      state.serialNumbers = [];
    },
    removeSelectedSerialNumbers(state, action: PayloadAction<string[]>): void {
      state.serialNumbers = state.serialNumbers.filter((serial) => !action.payload.includes(serial));
    },
    setSelectedReceipt(state, action: PayloadAction<POReceiptModel | null>): void {
      state.selectedReceipt = action.payload;
    },
    setStagedTable(state, action: PayloadAction<StagedTableModel | null>): void {
      if (action.payload === null) {
        state.stagedTable = [];
      } else {
        if (state.stagedTable.length) {
          if (action.payload === undefined) state.stagedTable = [];
          else {
            state.stagedTable.push(action.payload);
          }
        } else {
          state.stagedTable.push(action.payload);
        }
      }
    },
    setTotalPOReceipts(state, action: PayloadAction<number>): void {
      state.totalPOReceipts = action.payload;
    },
    resetFilters(state): void {
      state.filteredCUSTPONumberInput = '';
      state.filteredMDSIPONumberInput = '';
      state.filteredVendorAccountInput = '';
      state.filteredVendorNameInput = '';
      state.filteredWarehouseInput = '';
      state.dateRange = [];
      state.currentlyFiltering = false;
    },
    removeSelectedRows(state, action: PayloadAction<POStagedTableModel[]>): void {
      // Update the serial numbers inside the staged table
      const newTable = state.stagedTable.map((group) => {
        const newSerialNumbers = group.serialNumbers.filter((serialNumber) => {
          const itemFound = action.payload.find((rowToDelete) => rowToDelete.itemId === group.itemId && rowToDelete.serialNumber === serialNumber);

          return itemFound === undefined ? true : false;
        });

        const target = action.payload.find((rowToDelete) => rowToDelete.itemId === group.itemId);

        if (!newSerialNumbers.length && !group.isSerialized && target !== undefined) return { ...group, ...{ quantity: '0' } };
        else return { ...group, ...{ serialNumbers: newSerialNumbers } };
      });

      // Update the quantity inside the staged table
      const newTableCorrectQuantity = newTable.map((group) => {
        let amountToDelete = 0;

        action.payload.forEach((rowToDelete) => {
          if (rowToDelete.itemId === group.itemId) {
            if (group.isSerialized) amountToDelete = amountToDelete + 1;
          }
        });

        if (amountToDelete === 0) return group;
        else return { ...group, ...{ quantity: group.quantity } };
      });

      let newLinesForReceipt;

      // SINGLE DELETE USING TABLE ACTION (single only)
      if (action.payload.length === 1 && action.payload[0].serialNumber === '') {
        const lineToRemove = action.payload[0];

        newLinesForReceipt = state.selectedReceipt?.lines.map((line) => {
          if (lineToRemove.lineNumber === line.lineNumber) return { ...line, ...{ quantityStaged: line.quantityStaged } };
          else return line;
        });
      } else {
        // BULK DELETE USING DELETE BUTTON (single selected or multiple)
        newLinesForReceipt = state.selectedReceipt?.lines.map((line) => {
          let numberOfOccurences = 0;

          action.payload.forEach((rowToDelete) => {
            if (rowToDelete.lineNumber === line.lineNumber && line.isSerializedItem) numberOfOccurences = numberOfOccurences + 1;
            else if (rowToDelete.lineNumber === line.lineNumber && !line.isSerializedItem) numberOfOccurences = parseInt(rowToDelete.qty);
          });

          if (numberOfOccurences === 0) return line;
          else return { ...line, ...{ quantityStaged: line.quantityStaged - numberOfOccurences } };
        });
      }

      const updatedSelectedReceipt: POReceiptModel = {
        createdDateTime: state.selectedReceipt?.createdDateTime as string,
        deliveryMode: state.selectedReceipt?.deliveryMode as string,
        directDelivery: state.selectedReceipt?.directDelivery as string,
        lines: newLinesForReceipt as LinesModel[],
        orderedBy: state.selectedReceipt?.orderedBy as string,
        purchaseOrderNumber: state.selectedReceipt?.purchaseOrderNumber as string,
        requestedBy: state.selectedReceipt?.requestedBy as string,
        vendorAccount: state.selectedReceipt?.vendorAccount as string,
        vendorName: state.selectedReceipt?.vendorName as string,
        vendorOrderReference: state.selectedReceipt?.vendorOrderReference as string,
        warehouse: state.selectedReceipt?.warehouse as string
      };

      const updatedTable = newTableCorrectQuantity.filter((group) => {
        if (group.isSerialized && !group.serialNumbers.length) return false;
        else if (!group.isSerialized && group.quantity === '0') return false;
        else return true;
      });

      state.selectedReceipt = updatedSelectedReceipt;
      state.stagedTable = updatedTable.length ? updatedTable : [];
    },
    updateStagedQuantity(state, action: PayloadAction<{ itemId: string; quantity: number; initializeTable: boolean }>): void {
      let updateQuanity = false;

      const updatedReceiptLines = state.selectedReceipt?.lines.map((line) => {
        if (line.itemId !== action.payload.itemId) return line;
        else {
          line.quantityStaged = line.quantityStaged + action.payload.quantity;
          line.isSerializedItem ? (updateQuanity = true) : null;

          return line;
        }
      });

      const updatedSelectedReceipt: POReceiptModel = {
        createdDateTime: state.selectedReceipt?.createdDateTime as string,
        deliveryMode: state.selectedReceipt?.deliveryMode as string,
        directDelivery: state.selectedReceipt?.directDelivery as string,
        lines: updatedReceiptLines as LinesModel[],
        orderedBy: state.selectedReceipt?.orderedBy as string,
        purchaseOrderNumber: state.selectedReceipt?.purchaseOrderNumber as string,
        requestedBy: state.selectedReceipt?.requestedBy as string,
        vendorAccount: state.selectedReceipt?.vendorAccount as string,
        vendorName: state.selectedReceipt?.vendorName as string,
        vendorOrderReference: state.selectedReceipt?.vendorOrderReference as string,
        warehouse: state.selectedReceipt?.warehouse as string
      };

      if (!action.payload.initializeTable && updateQuanity) {
        const newStagedTable = state.stagedTable.map((group) => {
          if (group.itemId === action.payload.itemId) return { ...group, ...{ quantity: `${parseInt(group.quantity) + action.payload.quantity}` } };
          else return group;
        });

        state.stagedTable = newStagedTable;
      }
      state.selectedReceipt = updatedSelectedReceipt;
      state.selectedLine = { ...state.selectedLine, ...{ quantityOpen: (state.selectedLine?.quantityOpen as number) - action.payload.quantity } } as LinesModel;
    }
  },
  extraReducers: (builder) => {
    // FETCH RECEIPTS
    builder.addCase(asyncFetchPOReceipts.fulfilled, (state, action) => {
      const newPOReceipts: POReceiptModel[] = action.payload.map((receipt) => ({
        createdDateTime: receipt.createdDateTime,
        deliveryMode: receipt.deliveryMode,
        directDelivery: receipt.directDelivery,
        lines: receipt.lines,
        orderedBy: receipt.orderedBy,
        purchaseOrderNumber: receipt.purchaseOrderNumber,
        requestedBy: receipt.requestedBy,
        vendorAccount: receipt.vendorAccount,
        vendorName: receipt.vendorName,
        vendorOrderReference: receipt.vendorOrderReference,
        warehouse: receipt.warehouse
      }));

      const currentlyFilteringReceipts = Boolean(
        state.filteredMDSIPONumberInput.length ||
          state.filteredCUSTPONumberInput.length ||
          state.filteredVendorAccountInput.length ||
          state.filteredVendorNameInput.length ||
          state.filteredWarehouseInput.length ||
          state.dateRange.length
      );

      currentlyFilteringReceipts ? (state.currentlyFiltering = true) : (state.currentlyFiltering = false);

      if (state.currentlyFiltering) {
        state.POReceiptListFiltered.length ? (state.POReceiptListFiltered = state.POReceiptListFiltered.concat(newPOReceipts)) : (state.POReceiptListFiltered = newPOReceipts);
        state.POReceiptList = [];
        state.offset = state.offset + 25;

        if (state.totalCalls - 1 !== 0) {
          state.POReceiptListFiltered = [];
          state.offset = 0;
        }
      } else if (state.POReceiptList.length) {
        state.POReceiptList = state.POReceiptList.concat(newPOReceipts);
        state.offset = state.offset + 25;
        state.POReceiptListFiltered = [];
      } else {
        state.POReceiptList = newPOReceipts;
        state.POReceiptListFiltered = [];
        state.offset = state.offset + 25;
      }

      state.loading = false;
      state.totalCalls = state.totalCalls - 1;
    });
    builder.addCase(asyncFetchPOReceipts.pending, (state) => {
      const currentlyFilteringReceipts = Boolean(
        state.filteredMDSIPONumberInput.length ||
          state.filteredCUSTPONumberInput.length ||
          state.filteredVendorAccountInput.length ||
          state.filteredVendorNameInput.length ||
          state.filteredWarehouseInput.length ||
          state.dateRange.length
      );

      if (!state.POReceiptList.length && !currentlyFilteringReceipts) state.loading = true;
      else if (currentlyFilteringReceipts && !state.POReceiptListFiltered.length) state.loading = true;
      state.totalCalls = state.totalCalls + 1;
    });
    builder.addCase(asyncFetchPOReceipts.rejected, (state, action) => {
      state.error = action.payload as ErrorModel;
      state.loading = false;

      const enabled = process.env.REACT_APP_ROLLBAR_ENABLED;

      const reporter = new Rollbar({
        accessToken: process.env.REACT_APP_ROLLBAR_API_KEY,
        enabled: enabled === 'true' ? true : false,
        captureUncaught: true,
        captureUnhandledRejections: true,
        payload: {
          environment: process.env.REACT_APP_ROLLBAR_ENV
        }
      });

      reporter.error(action.payload as ErrorModel);
    });
    builder.addCase(asyncFetchPOLineNumber.fulfilled, (state, action) => {
      const lines = action.payload.lines.map((line) => {
        return { ...line, ...{ quantityStaged: 0 } };
      });

      state.allReceiptLines = action.payload.lines;

      if (action.payload !== null && state.selectedReceipt !== null) {
        state.selectedReceipt = { ...action.payload, ...{ lines: lines } };
        state.lineNumberLoading = false;
      } else if (action.payload !== null && state.selectedReceipt === null) {
        state.lineNumberLoading = false;
        state.selectedReceipt = { ...action.payload, ...{ lines: lines } };
      }
    });
    builder.addCase(asyncFetchPOLineNumber.pending, (state) => {
      state.lineNumberLoading = true;
    });
    builder.addCase(asyncFetchPOLineNumber.rejected, (state, action) => {
      state.fetchLineError = action.payload as ErrorModel;
      state.lineNumberLoading = false;

      const error = action.payload as ErrorModel;
      const enabled = process.env.REACT_APP_ROLLBAR_ENABLED;

      const reporter = new Rollbar({
        accessToken: process.env.REACT_APP_ROLLBAR_API_KEY,
        enabled: enabled === 'true' ? true : false,
        captureUncaught: true,
        captureUnhandledRejections: true,
        payload: {
          environment: process.env.REACT_APP_ROLLBAR_ENV
        }
      });

      reporter.error(action.payload as ErrorModel);
    });

    builder.addCase(asyncSubmitPackingSlip.fulfilled, (state) => {
      state.packingSlipSubmissionLoading = false;
      state.clearPOSubmissionData = true;
      state.stagedTable = [];
      state.selectedMenu = 'Main';
      message.success('Successfully Submited');
    });
    builder.addCase(asyncSubmitPackingSlip.pending, (state) => {
      state.packingSlipSubmissionLoading = true;
    });

    builder.addCase(asyncSubmitPackingSlip.rejected, (state, action) => {
      state.packingSlipSubmissionLoading = false;
      const error = action.payload as ErrorModel;
      const enabled = process.env.REACT_APP_ROLLBAR_ENABLED;

      const reporter = new Rollbar({
        accessToken: process.env.REACT_APP_ROLLBAR_API_KEY,
        enabled: enabled === 'true' ? true : false,
        captureUncaught: true,
        captureUnhandledRejections: true,
        payload: {
          environment: process.env.REACT_APP_ROLLBAR_ENV
        }
      });

      reporter.error(action.payload as ErrorModel);

      if (error.message !== '') message.error(error.message);
      else message.error(error.status + ' error');
    });
  }
});

export const {
  setClearPOSubmissionData,
  setDateRange,
  setLoading,
  setFilteredMDSIPONumberInput,
  setFilteredCUSTPONumberInput,
  setFilteredVendorAccountInput,
  setFilteredVendorNameInput,
  setFilteredWarehouseInput,
  setFetchLineError,
  setOffset,
  setPOReceiptList,
  setPOReceiptListFiltered,
  setStagedTable,
  setSelectedLine,
  setSelectedMenu,
  setSelectedReceipt,
  setTotalPOReceipts,
  removeSelectedRows,
  resetFilters,
  updateStagedQuantity,
  setSelectedToDelete,
  setSelectedKeysToDelete,
  setSerialNumbers,
  removerSerialNumber,
  removeAllSerialNumbers,
  removeSelectedSerialNumbers
} = poReceiptsSlice.actions;

export const poReceiptsReducer = poReceiptsSlice.reducer;
